import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, Check } from 'lucide-react';
import { useAuth } from '../../../components/AuthProvider';
import { apiUrl } from '../../../lib/api';

const STEP_LABELS = ['INTENT', 'DETAILS', 'EXECUTE'];

const fontMono = { fontFamily: "'Share Tech Mono', 'JetBrains Mono', 'Courier New', monospace" };
const fontOrbitron = { fontFamily: "'Orbitron', 'Arial', sans-serif" };
const fontRajdhani = { fontFamily: "'Rajdhani', 'Arial', sans-serif" };

const StepTracker = ({ currentStep }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0, marginBottom: 32 }}>
    {STEP_LABELS?.map((label, i) => {
      const isActive = i === currentStep;
      const isDone = i < currentStep;
      return (
        <React.Fragment key={i}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 36, height: 36, borderRadius: '50%',
              background: isDone ? '#2DC896' : isActive ? 'rgba(45,200,150,0.15)' : 'rgba(45,200,150,0.04)',
              border: `2px solid ${isDone ? '#2DC896' : isActive ? '#2DC896' : 'rgba(45,200,150,0.2)'}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: isActive ? '0 0 16px rgba(45,200,150,0.4)' : 'none', transition: 'all 0.3s ease' }}>
              {isDone ? <Check style={{ width: 14, height: 14, color: '#05060f' }} /> :
                <span style={{ ...fontMono, fontSize: 10, fontWeight: 700, color: isActive ? '#2DC896' : '#2DC89630', transition: 'all 0.3s' }}>{String(i + 1)?.padStart(2, '0')}</span>}
            </div>
            <span style={{ ...fontMono, fontSize: 9, letterSpacing: '0.12em', color: isActive ? '#2DC896' : isDone ? '#2DC89680' : '#2DC89630', transition: 'all 0.3s' }}>{label}</span>
          </div>
          {i < STEP_LABELS?.length - 1 && (
            <div style={{ width: 40, height: 1, background: i < currentStep ? '#2DC896' : 'rgba(45,200,150,0.15)', marginBottom: 22, transition: 'all 0.3s' }} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

const StepIntent = ({ intent, onNext }) => (
  <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.35 }}>
    <div style={{ marginBottom: 24 }}>
      <div style={{ ...fontOrbitron, fontSize: 11, color: '#2DC89660', letterSpacing: '0.2em', marginBottom: 8 }}>STEP 01 — INTENT</div>
      <h2 style={{ ...fontOrbitron, fontSize: 20, fontWeight: 700, color: '#2DC896', letterSpacing: '0.1em', marginBottom: 4 }}>MISSION DIRECTIVE</h2>
      <p style={{ ...fontMono, fontSize: 11, color: '#E8F4FF50' }}>Your intent has been captured. Review before proceeding.</p>
    </div>
    <div style={{ background: 'rgba(45,200,150,0.04)', border: '1px solid rgba(45,200,150,0.2)', borderRadius: 12, padding: '20px 24px', marginBottom: 32, borderLeft: '3px solid #2DC896' }}>
      <div style={{ ...fontMono, fontSize: 10, color: '#2DC89660', letterSpacing: '0.15em', marginBottom: 10 }}>◈ CAPTURED INTENT</div>
      <p style={{ ...fontRajdhani, fontSize: 16, color: '#E8F4FF', lineHeight: 1.7, fontWeight: 500 }}>{intent || 'No intent captured.'}</p>
    </div>
    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
      <button onClick={onNext}
        style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#2DC896', border: 'none', borderRadius: 10,
          padding: '12px 28px', ...fontOrbitron, fontSize: 12, fontWeight: 700, color: '#05060f', cursor: 'pointer',
          letterSpacing: '0.08em', boxShadow: '0 0 20px rgba(45,200,150,0.4)', transition: 'all 0.2s' }}>
        NEXT: DETAILS <ArrowRight style={{ width: 14, height: 14 }} />
      </button>
    </div>
  </motion.div>
);

const StepDetails = ({ intent, details, setDetails, onNext, onBack, onSkip }) => {
  const fields = [
    { key: 'goal', label: 'GOAL', placeholder: 'VORTEX will infer from your mission' },
    { key: 'targetAudience', label: 'TARGET AUDIENCE', placeholder: 'General business audience assumed' },
    { key: 'timeline', label: 'TIMELINE', placeholder: '14 days assumed' },
    { key: 'primaryChannels', label: 'PRIMARY CHANNELS', placeholder: 'All relevant channels' },
    { key: 'additionalContext', label: 'ADDITIONAL CONTEXT', placeholder: 'Optional — add anything specific', multiline: true },
  ];

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.35 }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{ ...fontOrbitron, fontSize: 11, color: '#2DC89660', letterSpacing: '0.2em', marginBottom: 8 }}>STEP 02 — DETAILS</div>
        <h2 style={{ ...fontOrbitron, fontSize: 20, fontWeight: 700, color: '#2DC896', letterSpacing: '0.1em', marginBottom: 4 }}>MISSION PARAMETERS</h2>
        <p style={{ ...fontMono, fontSize: 11, color: '#E8F4FF50' }}>VORTEX has pre-filled these based on your intent. Refine as needed.</p>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 32 }}>
        {fields?.map(field => (
          <div key={field?.key} style={{ gridColumn: field?.multiline ? '1 / -1' : 'auto' }}>
            <label style={{ ...fontMono, fontSize: 9, color: '#2DC89680', letterSpacing: '0.15em', display: 'block', marginBottom: 6 }}>{field?.label}</label>
            {field?.multiline ? (
              <textarea value={details?.[field?.key] || ''} onChange={e => setDetails(prev => ({ ...prev, [field?.key]: e?.target?.value }))}
                placeholder={field?.placeholder} rows={3}
                style={{ width: '100%', background: 'rgba(45,200,150,0.04)', border: '1px solid rgba(45,200,150,0.2)',
                  borderRadius: 8, padding: '10px 14px', ...fontMono, fontSize: 12, color: '#E8F4FF',
                  outline: 'none', resize: 'vertical', boxSizing: 'border-box', caretColor: '#2DC896', lineHeight: 1.6 }}
                onFocus={e => { e.target.style.borderColor = 'rgba(45,200,150,0.5)'; }}
                onBlur={e => { e.target.style.borderColor = 'rgba(45,200,150,0.2)'; }} />
            ) : (
              <input type="text" value={details?.[field?.key] || ''} onChange={e => setDetails(prev => ({ ...prev, [field?.key]: e?.target?.value }))}
                placeholder={field?.placeholder}
                style={{ width: '100%', background: 'rgba(45,200,150,0.04)', border: '1px solid rgba(45,200,150,0.2)',
                  borderRadius: 8, padding: '10px 14px', ...fontMono, fontSize: 12, color: '#E8F4FF',
                  outline: 'none', boxSizing: 'border-box', caretColor: '#2DC896' }}
                onFocus={e => { e.target.style.borderColor = 'rgba(45,200,150,0.5)'; }}
                onBlur={e => { e.target.style.borderColor = 'rgba(45,200,150,0.2)'; }} />
            )}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
        <button onClick={onSkip}
          style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'transparent', border: '1px solid #2DC896', borderRadius: 10,
            padding: '12px 22px', ...fontOrbitron, fontSize: 11, fontWeight: 700, color: '#2DC896', cursor: 'pointer',
            letterSpacing: '0.08em', transition: 'all 0.2s' }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(45,200,150,0.08)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
          SKIP &amp; EXECUTE →
        </button>
        <button onClick={onNext}
          style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#EF9F27', border: 'none', borderRadius: 10,
            padding: '12px 28px', ...fontOrbitron, fontSize: 11, fontWeight: 700, color: '#05060f', cursor: 'pointer',
            letterSpacing: '0.08em', boxShadow: '0 0 20px rgba(239,159,39,0.4)', transition: 'all 0.2s' }}>
          EXECUTE WITH DETAILS →
        </button>
      </div>
    </motion.div>
  );
};

const StepExecute = ({ intent, onComplete, useBusinessContext = true }) => {
  const { session } = useAuth();
  const [logLines, setLogLines] = useState([]);
  const [cursor, setCursor] = useState(true);
  const [done, setDone] = useState(false);
  const [orbPulse, setOrbPulse] = useState(true);
  const [error, setError] = useState(null);
  const logRef = useRef(null);
  const lineIdRef = useRef(0);
  const abortRef = useRef(null);
  const collectedCardsRef = useRef([]);

  const addLine = useCallback((text, color = '#2DC896', prefix = '◈') => {
    lineIdRef.current += 1;
    const now = new Date();
    const ts = `${now?.getHours()?.toString()?.padStart(2, '0')}:${now?.getMinutes()?.toString()?.padStart(2, '0')}:${now?.getSeconds()?.toString()?.padStart(2, '0')}`;
    setLogLines(prev => [...prev, { id: lineIdRef?.current, text, color, prefix, ts }]);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    abortRef.current = controller;
    collectedCardsRef.current = [];

    const runExecution = async () => {
      addLine('VORTEX selecting optimal operators...', '#2DC896', '◈');

      let resolvedMissionId = null;
      let resolvedPlan = null;

      try {
        addLine('Planning mission...', '#9B59FF', '⟳');
        const accessToken = session?.access_token;
        if (!accessToken) throw new Error('Authentication session is unavailable');
        const intentHeaders = {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        };
        const intentRes = await fetch(apiUrl('/api/intent'), {
          method: 'POST',
          headers: intentHeaders,
          body: JSON.stringify({ userText: intent || '' }),
          signal: controller?.signal,
        });

        if (!intentRes?.ok) throw new Error(`Intent API error HTTP ${intentRes?.status}`);

        const intentData = await intentRes?.json();
        resolvedMissionId = intentData?.mission_id;
        resolvedPlan = intentData?.plan;

        if (!resolvedMissionId || !resolvedPlan) throw new Error('Intent API returned no mission_id or plan');

        addLine(`Mission plan ready — ID: ${resolvedMissionId}`, '#2DC896', '✓');

        const executeBody = JSON.stringify({
            mission_id: resolvedMissionId,
            plan: resolvedPlan,
            use_business_context: useBusinessContext
          });
        const res = await fetch(apiUrl('/api/execute'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`
          },
          body: executeBody,
          signal: controller?.signal,
        });

        if (!res?.ok) throw new Error(`HTTP ${res?.status}: ${res?.statusText}`);

        if (!res?.body) throw new Error('Execution response did not include a stream');
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done: streamDone, value } = await reader.read();
          if (streamDone) break;

          buffer += decoder?.decode(value, { stream: true });
          const lines = buffer?.split('\n');
          buffer = lines?.pop() ?? '';

          for (const line of lines) {
            const trimmed = line?.trim();
            if (!trimmed || trimmed?.startsWith(':') || trimmed?.startsWith('event:')) continue;
            if (!trimmed?.startsWith('data:')) continue;

            const jsonStr = trimmed?.slice(5)?.trim();
            if (!jsonStr || jsonStr === '[DONE]') continue;

            try {
              const parsed = JSON.parse(jsonStr);
              const eventType = parsed?.event ?? parsed?.type ?? 'unknown';
              if (eventType === 'ping') continue;

              const data = parsed?.data ?? parsed;

              switch (eventType) {
                case 'planning':
                  addLine(`Execution plan validated — ${data?.total_steps ?? '?'} steps`, '#9B59FF', '◇');
                  break;
                case 'mission_start':
                  addLine(`Mission started — ${data?.total_steps ?? '?'} operators deploying`, '#2DC896', '◈');
                  break;
                case 'wave_begin':
                  addLine(`Wave ${data?.wave_index ?? '?'} of ${data?.total_waves ?? '?'} executing...`, '#9B59FF', '⟳');
                  break;
                case 'wave_start': addLine(`Wave executing...`, '#9B59FF', '⟳');
                  break;
                case 'bot_selected':
                  addLine(`[${data?.bot_name || data?.bot_id || 'Operator'}] selected`, '#2DC896', '◇');
                  break;
                case 'step_start':
                  addLine(`[${data?.bot_name || data?.assigned_bot || data?.bot_id || 'Executing'}] → ${data?.action ?? data?.step ?? ''}...`, '#2DC896', '🤖');
                  break;
                case 'partial_output':
                  addLine(`${data?.step_id ?? 'Operator'} output received`, '#2DC89680', '…');
                  break;
                case 'verification':
                  addLine(`Verification started for ${data?.step_id ?? ''}`, '#9B59FF', '🔍');
                  break;
                case 'verify_start':
                  addLine(`Verifying ${data?.step_id ?? ''}...`, '#9B59FF', '🔍');
                  break;
                case 'verify_pass':
                  addLine(`${data?.step_id ?? ''} verified ✓`, '#00FF88', '✅');
                  break;
                case 'verify_fail':
                  addLine(`Retrying ${data?.step_id ?? ''}...`, '#EF9F27', '⚠️');
                  break;
                case 'step_complete':
                  if (data?.status === 'complete') {
                    addLine(`${data?.step_id ?? ''} complete`, '#00FF88', '✓');
                  } else {
                    addLine(`${data?.step_id ?? ''} ${data?.status || 'failed'}`, '#FF4444', '✗');
                  }
                  if (data?.card && data?.status === 'complete') {
                    collectedCardsRef?.current?.push(data?.card);
                    addLine(`Output ready: ${data?.card?.title || 'file'}`, '#EF9F27', '📄');
                  }
                  break;
                case 'action_card':
                  if (data?.card) {
                    collectedCardsRef?.current?.push(data?.card);
                    addLine(`Output ready: ${data?.card?.title || 'file'}`, '#EF9F27', '📄');
                  }
                  break;
                case 'step_failed':
                  addLine(`${data?.step_id ?? ''} failed`, '#FF4444', '✗');
                  break;
                case 'wave_complete':
                  addLine(`Wave ${data?.wave_index ?? ''} complete`, '#00FF88', '✓');
                  break;
                case 'files_ready':
                  if (data?.files && Array.isArray(data?.files)) {
                    collectedCardsRef.current.__filesReady = data?.files;
                    addLine(`${data?.files?.length} file${data?.files?.length !== 1 ? 's' : ''} ready for download`, '#EF9F27', '📁');
                  }
                  break;
                case 'execution_complete':
                case 'mission_complete': {
                  addLine('All operators complete', '#2DC896', '◉');
                  addLine(`Mission complete — ${collectedCardsRef?.current?.length} files ready`, '#EF9F27', '⚡');
                  setDone(true);
                  setOrbPulse(false);
                  const finalCards = [...collectedCardsRef?.current];
                  const filesReady = collectedCardsRef?.current?.__filesReady || null;
                  setTimeout(() => {
                    onComplete({
                      actionCards: finalCards,
                      completedSteps: data?.completed_steps || {},
                      mission_id: resolvedMissionId,
                      steps: data?.steps || [],
                      summary_strategic: data?.summary_strategic || null,
                      summary_simple: data?.summary_simple || null,
                      steps_completed: data?.steps_completed || finalCards?.length,
                      steps_failed: data?.steps_failed || 0,
                      total_cards: data?.total_cards || finalCards?.length,
                      filesReady: filesReady,
                    });
                  }, 1200);
                  return;
                }
                case 'critical_failure':
                  addLine(`Critical failure: ${data?.message || data?.error || 'Mission requirement failed'}`, '#FF4444', '✗');
                  break;
                case 'mission_failed': {
                  const failureMessage = data?.message || data?.error || 'Mission execution failed';
                  addLine(`Mission failed: ${failureMessage}`, '#FF4444', '✗');
                  setError(failureMessage);
                  setDone(true);
                  setOrbPulse(false);
                  return;
                }
                case 'mission_error': case 'execution_error':
                  addLine(`Error: ${data?.error ?? 'Unknown error'}`, '#FF4444', '✗');
                  setError(data?.error ?? 'Execution failed');
                  setDone(true);
                  setOrbPulse(false);
                  return;
                default:
                  break;
              }
            } catch {
              // Non-JSON line, skip
            }
          }
        }

        addLine('Execution stream ended before a terminal event', '#EF9F27', '⚠️');
        const finalCards = [...collectedCardsRef?.current];

        if (finalCards?.length === 0 && resolvedMissionId) {
          addLine('Fetching results from API...', '#EF9F27', '⟳');
          try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const missionRes = await fetch(apiUrl(`/api/missions/${resolvedMissionId}`), { headers });
            if (missionRes?.ok) {
              const missionData = await missionRes?.json();
              const missionStatus = missionData?.mission?.status || missionData?.status;
              if (missionStatus && missionStatus !== 'complete') {
                throw new Error(`Mission ended with status: ${missionStatus}`);
              }
              const steps = missionData?.mission?.results || missionData?.results || [];
              const apiCards = steps?.filter(s => s?.output && s?.output?.trim()?.length > 0)?.map((s, i) => ({
                id: s?.id || `card_${i}`,
                title: s?.action || s?.id || `Step ${i + 1}`,
                bot_name: s?.bot_name || s?.bot_id || 'Operator',
                full_output: s?.output,
                output: s?.output,
                description: (s?.output || '')?.slice(0, 300),
                status: 'done',
              }));
              if (apiCards?.length > 0) {
                addLine(`Retrieved ${apiCards?.length} outputs from API ✓`, '#2DC896', '📄');
                setTimeout(() => onComplete({
                  actionCards: apiCards,
                  completedSteps: {},
                  mission_id: resolvedMissionId,
                }), 500);
                return;
              }
            }
          } catch (e) {
            throw new Error(e?.message || 'Mission result recovery failed');
          }
        }
        throw new Error('Execution stream ended without mission_complete');

      } catch (err) {
        if (err?.name === 'AbortError') return;
        addLine(`Connection error: ${err?.message || 'Failed'}`, '#FF4444', '✗');
        setError(err?.message);
        setDone(true);
        setOrbPulse(false);
      }
    };

    runExecution();
    return () => { abortRef?.current?.abort(); };
  }, []);

  useEffect(() => {
    if (logRef?.current) logRef.current.scrollTop = logRef?.current?.scrollHeight;
  }, [logLines]);

  useEffect(() => {
    const interval = setInterval(() => setCursor(c => !c), 500);
    return () => clearInterval(interval);
  }, []);

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.35 }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{ ...fontOrbitron, fontSize: 11, color: '#EF9F2760', letterSpacing: '0.2em', marginBottom: 8 }}>STEP 03 — EXECUTE</div>
        <h2 style={{ ...fontOrbitron, fontSize: 20, fontWeight: 700, color: '#EF9F27', letterSpacing: '0.1em', marginBottom: 4 }}>MISSION IN PROGRESS</h2>
        <p style={{ ...fontMono, fontSize: 11, color: '#E8F4FF50' }}>VORTEX is deploying the best operators for your mission.</p>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
        <div style={{ position: 'relative', width: 80, height: 80 }}>
          <style>{`
            @keyframes mfm-orb-pulse { 0%, 100% { transform: scale(1); opacity: 0.8; } 50% { transform: scale(1.15); opacity: 1; } }
            @keyframes mfm-ring-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
            @keyframes mfm-ring-spin-rev { from { transform: rotate(0deg); } to { transform: rotate(-360deg); } }
          `}</style>
          <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '1.5px solid rgba(239,159,39,0.4)', animation: 'mfm-ring-spin 3s linear infinite' }} />
          <div style={{ position: 'absolute', inset: 8, borderRadius: '50%', border: '1px solid rgba(239,159,39,0.25)', animation: 'mfm-ring-spin-rev 2s linear infinite' }} />
          <div style={{ position: 'absolute', inset: 20, borderRadius: '50%', background: 'radial-gradient(circle, #EF9F27 0%, rgba(239,159,39,0.4) 60%, transparent 100%)', animation: orbPulse ? 'mfm-orb-pulse 0.8s ease-in-out infinite' : 'none', boxShadow: '0 0 20px rgba(239,159,39,0.6)' }} />
        </div>
      </div>
      <div style={{ background: '#030408', border: '1px solid rgba(45,200,150,0.15)', borderRadius: 12, overflow: 'hidden', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderBottom: '1px solid rgba(45,200,150,0.08)', background: 'rgba(45,200,150,0.03)' }}>
          <div style={{ width: 7, height: 7, borderRadius: '50%', background: error ? '#FF4444' : '#00FF88', boxShadow: `0 0 6px ${error ? '#FF4444' : '#00FF88'}`, animation: 'mfm-orb-pulse 1.5s ease-in-out infinite' }} />
          <span style={{ ...fontMono, fontSize: 10, color: '#2DC896', letterSpacing: '0.15em' }}>LIVE EXECUTION LOG</span>
          <span style={{ ...fontMono, fontSize: 9, color: '#2DC89640', marginLeft: 'auto' }}>{logLines?.length} EVENTS</span>
        </div>
        <div ref={logRef} style={{ maxHeight: 300, overflowY: 'auto', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {logLines?.map((line, i) => (
            <div key={line?.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, opacity: 0, animation: `mfm-feed-in 0.3s ease-out ${i * 0.05}s forwards` }}>
              <style>{`@keyframes mfm-feed-in { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }`}</style>
              <span style={{ ...fontMono, fontSize: 9, color: '#2DC89630', flexShrink: 0, paddingTop: 1, minWidth: 60 }}>{line?.ts}</span>
              <span style={{ ...fontMono, fontSize: 10, color: '#2DC89640', flexShrink: 0 }}>{line?.prefix}</span>
              <span style={{ ...fontMono, fontSize: 11, color: line?.color, lineHeight: 1.5 }}>{line?.text}</span>
            </div>
          ))}
          {!done && <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 70 }}><span style={{ ...fontMono, fontSize: 11, color: '#2DC89680' }}>Processing{cursor ? '█' : ' '}</span></div>}
          {done && !error && <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 70 }}><span style={{ ...fontMono, fontSize: 11, color: '#EF9F27' }}>Preparing output files...</span></div>}
          {done && error && <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 70 }}><span style={{ ...fontMono, fontSize: 11, color: '#FF4444' }}>Execution ended with errors.</span></div>}
        </div>
      </div>
    </motion.div>
  );
};

const MissionFlowModal = ({ isOpen, onClose, intent, missionPlan, onMissionComplete, onStepChange, useBusinessContext = true }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [details, setDetails] = useState({});

  useEffect(() => {
    if (isOpen && intent) {
      const lower = intent?.toLowerCase();
      setDetails({
        goal: intent,
        targetAudience: lower?.includes('b2b') ? 'B2B businesses' : lower?.includes('consumer') ? 'Consumers' : 'Target audience',
        timeline: lower?.includes('week') ? '7 days' : lower?.includes('month') ? '30 days' : '14 days',
        primaryChannels: lower?.includes('email') ? 'Email, social media' : lower?.includes('social') ? 'Social media, content' : 'Email, social media, SEO',
        additionalContext: ''
      });
      setCurrentStep(1);
    }
  }, [isOpen, intent]);

  const handleClose = () => {
    setCurrentStep(0);
    onStepChange?.(null);
    onClose();
  };

  const handleExecuteComplete = (executionResult = {}) => {
    onMissionComplete?.({
      intent,
      completedSteps: executionResult?.completedSteps || {},
      actionCards: executionResult?.actionCards || [],
      mission_id: executionResult?.mission_id || null,
      steps: executionResult?.steps || [],
      summary_strategic: executionResult?.summary_strategic || null,
      summary_simple: executionResult?.summary_simple || null,
      steps_completed: executionResult?.steps_completed || executionResult?.actionCards?.length || 0,
      steps_failed: executionResult?.steps_failed || 0,
      total_cards: executionResult?.total_cards || executionResult?.actionCards?.length || 0,
    });
    setTimeout(() => { handleClose(); }, 2000);
  };

  if (!isOpen) return null;

  const goToStep = (step) => {
    setCurrentStep(step);
    onStepChange?.(step);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(5,6,15,0.92)', backdropFilter: 'blur(8px)', overflowY: 'auto' }}>
          <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0 }}>
            {Array.from({ length: 60 }, (_, i) => (
              <div key={i} style={{ position: 'absolute', left: `${(i * 17.3) % 100}%`, top: `${(i * 23.7) % 100}%`, width: i % 4 === 0 ? 2 : 1, height: i % 4 === 0 ? 2 : 1, borderRadius: '50%', background: i % 5 === 0 ? '#2DC896' : '#ffffff', opacity: 0.08 + (i % 5) * 0.04 }} />
            ))}
          </div>
          <motion.div initial={{ opacity: 0, y: 24, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            style={{ position: 'relative', zIndex: 1, width: '92%', maxWidth: 780, margin: '70px auto 40px auto', background: 'rgba(5,6,15,0.96)', border: '1px solid rgba(45,200,150,0.2)', borderRadius: 12, boxShadow: '0 0 80px rgba(45,200,150,0.08), 0 0 160px rgba(0,0,0,0.8)', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', borderBottom: '1px solid rgba(45,200,150,0.1)', background: 'rgba(45,200,150,0.03)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ display: 'flex', gap: 6 }}>
                  {['#ff5f57', '#febc2e', '#28c840']?.map((c, i) => <div key={i} style={{ width: 10, height: 10, borderRadius: '50%', background: c, opacity: 0.8 }} />)}
                </div>
                <span style={{ ...fontOrbitron, fontSize: 11, fontWeight: 700, color: '#2DC896', letterSpacing: '0.15em', marginLeft: 8 }}>VORTEX MISSION FLOW</span>
              </div>
              <button onClick={handleClose}
                style={{ background: 'rgba(45,200,150,0.06)', border: '1px solid rgba(45,200,150,0.15)', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', color: '#2DC89680', display: 'flex', alignItems: 'center', gap: 4, ...fontMono, fontSize: 10 }}>
                <X style={{ width: 13, height: 13 }} /> ESC
              </button>
            </div>
            <div style={{ padding: '32px 40px 40px' }}>
              <StepTracker currentStep={currentStep} />
              <AnimatePresence mode="wait">
                {currentStep === 1 && <StepDetails key="details" intent={intent} details={details} setDetails={setDetails} onNext={() => goToStep(2)} onBack={() => goToStep(0)} onSkip={() => goToStep(2)} />}
                {currentStep === 2 && <StepExecute key="execute" intent={intent} onComplete={handleExecuteComplete} useBusinessContext={useBusinessContext} />}
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MissionFlowModal;
